/**
 * sortSelectOptions.js
 * Helper unique pour trier les options des menus déroulants (listes SANS ordre naturel).
 *
 * Règles :
 *  - Placeholder / valeur vide → EN HAUT
 *  - « Autre » (et variantes : "Autre", "Autre XXX", "Autre (préciser)", codes autre_*) → EN BAS
 *  - Reste trié A→Z avec localeCompare('fr') (accents gérés)
 *
 * ⚠️ NE PAS UTILISER sur les listes à ordre logique (échelles, workflows, ordinaux,
 * statuts, niveaux, mois, priorités, etc.) — voir doc dans le task brief.
 *
 * Formats d'entrée supportés :
 *   1. Array<string>                 → ["Paris", "Lyon", ...]
 *   2. Array<{value, label}>         → [{value:"paris", label:"Paris"}, ...]
 *   3. Array<{code, label}>          → variantes CRM (code + label)
 *   4. Array<[value, label]>         → forme tuple (Object.entries)
 *   5. Record<string, string>        → { paris: "Paris", lyon: "Lyon" }  (Object)
 *
 * Retourne un array (ou Object si l'entrée est un Object) avec le même format.
 */

const _getLabel = (o) => {
  if (o == null) return '';
  if (typeof o === 'string') return o;
  if (Array.isArray(o)) {
    // Tuple [value, label|objet] : si o[1] est un objet avec .label (ex. Object.entries de {code: {label:'...', ...}}), l'extraire
    const v = o[1];
    if (v == null) return String(o[0] ?? '');
    if (typeof v === 'object') return String(v.label ?? v.name ?? o[0] ?? '');
    return String(v);
  }
  return String(o.label ?? o.name ?? o.value ?? o.code ?? '');
};

const _getValue = (o) => {
  if (o == null) return '';
  if (typeof o === 'string') return o;
  if (Array.isArray(o)) return String(o[0] ?? '');
  return String(o.value ?? o.code ?? o.id ?? '');
};

const _isPlaceholder = (o) => {
  const v = _getValue(o);
  const l = _getLabel(o).trim().toLowerCase();
  // Valeur vide = placeholder
  if (v === '' || v === null || v === undefined) return true;
  // Labels placeholder courants FR
  return (
    l === '' ||
    l.startsWith('sélectionner') ||
    l.startsWith('selectionner') ||
    l.startsWith('— sélectionner') ||
    l.startsWith('-- sélectionner') ||
    l === 'tous' ||
    l === 'toutes' ||
    l === 'tout' ||
    l === 'non spécifié' ||
    l === 'non specifie' ||
    l === '—' ||
    l === '--' ||
    l === 'aucun'
  );
};

const _isAutre = (o) => {
  const v = _getValue(o).toLowerCase();
  const l = _getLabel(o).trim().toLowerCase();
  // Code : "autre" ou commence par "autre_" ou finit par "_autre"
  if (v === 'autre' || v.startsWith('autre_') || v.endsWith('_autre')) return true;
  // Label : commence par "Autre" (ex. "Autre", "Autre profession", "Autre (préciser)")
  return l === 'autre' || l.startsWith('autre ') || l.startsWith('autre(') || l.startsWith('autre,');
};

const _cmpFR = (a, b) => _getLabel(a).localeCompare(_getLabel(b), 'fr', { sensitivity: 'base', numeric: true });

/**
 * Trie une liste d'options selon les 3 règles (placeholder haut, autre bas, alpha FR).
 * @param {Array|Object} options
 * @returns {Array|Object} même forme que l'entrée
 */
export function sortSelectOptions(options) {
  if (!options) return options;

  // Si Object (Record), convertir → array → retrier → reconvertir Object (préserve ordre en JS)
  if (!Array.isArray(options) && typeof options === 'object') {
    const entries = Object.entries(options); // [ [k,v], ... ]
    const sorted = sortSelectOptions(entries); // récursif sur array de tuples
    return Object.fromEntries(sorted);
  }

  if (!Array.isArray(options)) return options;

  const placeholders = [];
  const autres = [];
  const rest = [];

  for (const opt of options) {
    if (_isPlaceholder(opt)) placeholders.push(opt);
    else if (_isAutre(opt)) autres.push(opt);
    else rest.push(opt);
  }

  rest.sort(_cmpFR);
  autres.sort(_cmpFR); // au cas où plusieurs "Autre" (ex: "Autre profession", "Autre activité")
  return [...placeholders, ...rest, ...autres];
}

export default sortSelectOptions;
